import { useEffect, useRef, useState } from 'react';

import { useIsFocused } from '@react-navigation/native';
import { useLocalSearchParams, usePathname, useRouter, useSegments } from 'expo-router';
import { useStoreContext } from 'features/store';

import { isMenuObjectDetailRoute } from 'navigation/routing/utils';
import { appendObjectToQueryString } from 'navigation/utils';
import { routes } from 'utils/routing';

/** If no store is selected...
 * pops a store locator 1 time per screen mount, if you close it, navigate elsewhere and come back it will pop again 1 time.
 * Transparent drawer overlays (hamburger menu, notification inbox) do not count as navigating away.
 * Unfocused screens never navigate: NativeTabs eagerly mounts every tab, so Menu/My Code/Offers
 * would otherwise open the locator after logout while Home is showing. */
export const useStoreRequired = (
  isRequired = true,
  redirectParams?: Record<string, unknown>,
  tabPathname?: string
) => {
  const { noStoreSelected } = useStoreContext();
  const pathname = usePathname();
  const segments = useSegments();
  const params = useLocalSearchParams();
  const router = useRouter();
  const isFocused = useIsFocused();
  const didPopModalRef = useRef(false);
  const tabPathnameRef = useRef(tabPathname ?? pathname);
  const redirectUrl = redirectParams
    ? appendObjectToQueryString(pathname, redirectParams)
    : pathname;

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    // to allow this to run in _layout files which run before normal react code and causes errors without this.
    setIsMounted(true);
  }, []);

  useEffect(() => {
    // Drawer routes (account / notification-inbox / support / …) are transparent overlays over the
    // still-mounted tab. Treat them as staying on the same tab so the one-shot latch is preserved.
    const isDrawerOverlay = segments[0] === '(drawer)';
    const didNavigateAway =
      !isDrawerOverlay &&
      !pathname?.startsWith(routes.storeLocator) &&
      !pathname.startsWith(routes.minimalStoreLocator) &&
      !pathname.startsWith(tabPathnameRef.current);

    if (didNavigateAway) {
      // another screen has been pushed. unfortunately the cleanup of useFocusEffect isn't working reliably for this
      didPopModalRef.current = false;
    }
  }, [pathname, segments]);

  useEffect(() => {
    if (!isMounted || !isRequired || !isFocused) {
      return;
    }

    // Menu detail modals own store selection (info modal + CTA). Never
    // auto-pop the full locator over them, even if a parent still passes required.
    if (isMenuObjectDetailRoute(pathname)) {
      return;
    }

    const isSelectingStore =
      pathname.startsWith(routes.storeLocator) || pathname.startsWith(routes.minimalStoreLocator);
    if (
      noStoreSelected &&
      !isSelectingStore &&
      !didPopModalRef.current &&
      pathname.startsWith(tabPathnameRef.current)
    ) {
      didPopModalRef.current = true;
      router.navigate(
        appendObjectToQueryString(routes.storeLocator, {
          ...params,
          redirectUrl,
        })
      );
    }
  }, [isMounted, isRequired, isFocused, noStoreSelected, params, pathname, redirectUrl, router]);

  return { noStoreSelected };
};
